import { useRef, useState } from 'react'

import { EMPTY_STRING } from '../../../../../../../constants/common'
import {
  EVENT_NAME_KEY_DOWN,
  EVENT_NAME_MOUSE_DOWN,
} from '../../../../../../../constants/event'
import { useEventListener, useNotification } from '../../../../../../../hooks'
import { isEventKeyEscape } from '../../../../../../../utils/events'
import { patchMockApplicant } from '../../../../../../RentalPortalApplicant'

export function useApplicantProfileNoteEdit({
  applicantId,
  handleEntityReload,
  value,
}) {
  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const [isEditing, setEditing] = useState(false)
  const [isSaving, setSaving] = useState(false)
  const [editValue, setEditValue] = useState(null)

  const editContainerRef = useRef(null)
  const isCommittingRef = useRef(false)

  function handleStartEditing() {
    setEditValue(value ?? EMPTY_STRING)
    setEditing(true)
  }

  function handleChangeEditValue(nextValue) {
    setEditValue(nextValue)
  }

  async function handleSaveEditValue() {
    if (!isCommittingRef.current) {
      isCommittingRef.current = true

      if (editValue.trim() === (value ?? EMPTY_STRING).trim()) {
        setEditing(false)
        isCommittingRef.current = false

        return
      }

      setSaving(true)

      try {
        await patchMockApplicant(applicantId, {
          content: editValue.trim() || null,
        })
        await handleEntityReload()

        handleAddSuccessNotification('Notification.CommonTitleSavedKey')

        setEditing(false)
      } catch {
        handleAddErrorNotification(
          'RentalPortal.ApplicantProfileNoteSaveErrorKey'
        )
      } finally {
        setSaving(false)
        isCommittingRef.current = false
      }
    }
  }

  function handleDocumentMouseDown(event) {
    if (isEditing && !editContainerRef.current?.contains(event.target)) {
      handleSaveEditValue()
    }
  }

  function handleDocumentKeyDown(event) {
    if (isEditing && isEventKeyEscape(event)) {
      setEditing(false)
    }
  }

  useEventListener({
    eventName: EVENT_NAME_MOUSE_DOWN,
    handler: handleDocumentMouseDown,
  })

  useEventListener({
    eventName: EVENT_NAME_KEY_DOWN,
    handler: handleDocumentKeyDown,
  })

  return {
    editContainerRef,
    editValue,
    handleChangeEditValue,
    handleSaveEditValue,
    handleStartEditing,
    isEditing,
    isSaving,
  }
}
